import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorIcon from "@mui/icons-material/Error";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ListIcon from "@mui/icons-material/List";
import ReorderIcon from "@mui/icons-material/Reorder";
import { useParams, useNavigate } from "react-router-dom";
import { base_url } from "../config/config";

const AnfrageInvoiceUpload = () => {
  const { anf } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const navigate = useNavigate();
  let selectedANRForQR = anf;
  useEffect(() => {
    if (!selectedANRForQR) {
      setMessage(
        "Keine Anfrage-ID gefunden. Bitte scannen Sie einen gültigen QR-Code."
      );
      setSeverity("error");
    }
  }, [selectedANRForQR]);

  // Calculate overall progress
  const getOverallProgress = () => {
    if (files.length === 0) return 0;
    const totalProgress = files.reduce((sum, file) => sum + file.progress, 0);
    return totalProgress / files.length;
  };

  const completedFiles = files.filter(f => f.status === 'completed').length;
  const hasCompletedFiles = completedFiles > 0;
  const hasPendingFiles = files.filter(f => f.status === 'pending').length > 0;

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files).map((file, index) => ({
        file,
        id: Date.now() + index,
        sequenceNumber: files.length + index + 1,
        status: 'pending', // pending, uploading, completed, error
        progress: 0,
        uploadedAt: null,
      }));
      setFiles(prev => [...prev, ...newFiles]);
      setMessage("");
    }
  };

  const simulateProgress = () => {
    let progress = 0;
    const timer = setInterval(() => {
      progress += Math.random() * 10;
      if (progress > 95) {
        clearInterval(timer);
        progress = 95;
      }
      setUploadProgress(progress);
    }, 300);
    return timer;
  };

  const handleUploadAll = async () => {
    if (files.length === 0 || !selectedANRForQR) {
      setMessage(
        "Bitte wählen Sie mindestens eine Datei aus oder es fehlt die Anfrage-ID"
      );
      setSeverity("warning");
      return;
    }

    setUploading(true);
    setMessage(`Uploading ${files.length} Dateien...`);
    setSeverity("info");
    setCurrentUploadIndex(0);

    const pendingFiles = files.filter(f => f.status === 'pending');
    
    try {
      for (let i = 0; i < pendingFiles.length; i++) {
        const fileData = pendingFiles[i];
        setCurrentUploadIndex(i + 1);
        
        // Update file status to uploading
        setFiles(prev => prev.map(f => 
          f.id === fileData.id 
            ? { ...f, status: 'uploading' }
            : f
        ));

        setMessage(`Uploading Seite ${fileData.sequenceNumber} (${i + 1}/${pendingFiles.length})...`);
        
        // Simulate progress for this file
        const progressTimer = setInterval(() => {
          setFiles(prev => prev.map(f => {
            if (f.id === fileData.id && f.progress < 90) {
              return { ...f, progress: f.progress + Math.random() * 20 };
            }
            return f;
          }));
        }, 200);

        try {
          await uploadSingleFile(fileData);
          
          clearInterval(progressTimer);
          
          // Mark file as completed
          setFiles(prev => prev.map(f => 
            f.id === fileData.id 
              ? { 
                  ...f, 
                  status: 'completed', 
                  progress: 100, 
                  uploadedAt: new Date().toLocaleString()
                }
              : f
          ));
          
          setUploadedFiles(prev => [...prev, fileData]);
          
        } catch (error) {
          clearInterval(progressTimer);
          
          // Mark file as error
          setFiles(prev => prev.map(f => 
            f.id === fileData.id 
              ? { ...f, status: 'error', progress: 0 }
              : f
          ));
          
          throw error;
        }
        
        // Small delay between uploads
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      const completedCount = files.filter(f => f.status === 'completed').length + pendingFiles.length;
      setMessage(`Alle ${completedCount} Dateien erfolgreich hochgeladen!`);
      setSeverity("success");
      
    } catch (error) {
      console.error(error);
      setMessage("Fehler beim Hochladen: " + error.message);
      setSeverity("error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setCurrentUploadIndex(0);
    }
  };

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const newFiles = droppedFiles.map((file, index) => ({
        file,
        id: Date.now() + index,
        sequenceNumber: files.length + index + 1,
        status: 'pending',
        progress: 0,
        uploadedAt: null,
      }));
      setFiles(prev => [...prev, ...newFiles]);
      setMessage("");
    }
  };

  // Remove file from list
  const removeFile = (fileId) => {
    setFiles(prev => {
      const newFiles = prev.filter(f => f.id !== fileId);
      // Resequence remaining files
      return newFiles.map((file, index) => ({
        ...file,
        sequenceNumber: index + 1,
      }));
    });
  };

  // Upload single file
  const uploadSingleFile = async (fileData) => {
    const formData = new FormData();
    formData.append("document", fileData.file);
    formData.append("sequenceNumber", fileData.sequenceNumber);
    formData.append("fileName", `Seite_${fileData.sequenceNumber}_${fileData.file.name}`);

    const response = await fetch(
      `${base_url}/v1/upload/documentToAnfrage?ANF=${selectedANRForQR}&categoryId=67bcd858f17e9732596626eb`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload für Seite ${fileData.sequenceNumber} fehlgeschlagen`);
    }

    return response.json();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
        py: isMobile ? 2 : 4,
        px: isMobile ? 1 : 3,
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Fade in timeout={800}>
          <Paper
            elevation={isMobile ? 2 : 6}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            }}
          >
            {/* Top Bar */}
            <Box
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                color: 'white',
                p: isMobile ? 2 : 3,
                position: 'relative',
              }}
            >
              <IconButton
                onClick={() => window.history.back()}
                sx={{
                  position: 'absolute',
                  left: isMobile ? 8 : 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    margin: '0 auto',
                    mb: 2,
                    width: isMobile ? 56 : 64,
                    height: isMobile ? 56 : 64,
                  }}
                >
                  <QrCodeIcon sx={{ fontSize: isMobile ? 28 : 32 }} />
                </Avatar>
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  fontWeight="bold"
                  gutterBottom
                >
                  Mehrfach-Upload
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Hochladen mehrerer Dokumente mit Seitennummern
                </Typography>
              </Box>
            </Box>

            {/* Content */}
            <CardContent sx={{ p: isMobile ? 2 : 4 }}>
              {selectedANRForQR && (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    borderColor: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}10`,
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="primary" />
                      <Typography variant="h6" color="primary" fontWeight="medium">
                        Anfrage: {selectedANRForQR}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}

              {message && (
                <Fade in timeout={400}>
                  <Alert
                    severity={severity}
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      fontSize: isMobile ? '0.9rem' : '1rem',
                    }}
                  >
                    {message}
                  </Alert>
                </Fade>
              )}

              {hasCompletedFiles && (
                <Fade in timeout={600}>
                  <Card variant="outlined" sx={{ mb: 3, borderColor: 'success.main', backgroundColor: 'success.light', color: 'success.contrastText' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CheckCircleIcon color="success" />
                        <Typography variant="h6" fontWeight="bold">
                          {completedFiles} Datei(en) erfolgreich hochgeladen!
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Die Dateien wurden der Anfrage {selectedANRForQR} zugeordnet.
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              )}

              {/* Upload Area */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                <>
                  {/* Upload Area */}
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: `2px dashed ${
                        isDragOver
                          ? theme.palette.primary.main
                          : theme.palette.grey[300]
                      }`,
                      borderRadius: 3,
                      p: isMobile ? 2 : 4,
                      mb: 3,
                      textAlign: "center",
                      backgroundColor: isDragOver
                        ? `${theme.palette.primary.main}08`
                        : theme.palette.grey[50],
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: `${theme.palette.primary.main}05`,
                        borderColor: theme.palette.primary.light,
                      },
                    }}
                  >
                    <input
                      type="file"
                      id="anfrage-document-upload"
                      style={{ display: "none" }}
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.dwg,.dxf"
                      multiple
                    />
                    
                    <CloudUploadIcon
                      sx={{
                        fontSize: isMobile ? 48 : 64,
                        color: theme.palette.primary.main,
                        mb: 2,
                      }}
                    />
                    
                    <Typography
                      variant={isMobile ? 'h6' : 'h5'}
                      fontWeight="medium"
                      gutterBottom
                    >
                      {isDragOver ? 'Datei hier ablegen' : 'Dokument hochladen'}
                    </Typography>
                    
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                      Ziehen Sie eine Datei hierher oder klicken Sie zum Auswählen
                    </Typography>
                    
                    <label htmlFor="anfrage-document-upload">
                      <Button
                        component="span"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        size={isMobile ? 'large' : 'medium'}
                        sx={{
                          borderRadius: 2,
                          px: isMobile ? 4 : 6,
                          py: isMobile ? 1.5 : 1,
                        }}
                      >
                        Datei auswählen
                      </Button>
                    </label>
                    
                    <Typography variant="caption" display="block" sx={{ mt: 2, color: theme.palette.text.secondary }}>
                      Unterstützte Formate: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, DWG, DXF
                    </Typography>
                  </Box>
                  </>
                </CardContent>
              </Card>

              {/* File List */}
              {files.length > 0 && (
                <Fade in timeout={400}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <ListIcon color="primary" />
                        <Typography variant="h6" fontWeight="medium">
                          Dateiliste ({files.length} Dateien)
                        </Typography>
                      </Box>
                      
                      <Stack spacing={2}>
                        {files.map((fileData, index) => (
                          <Card key={fileData.id} variant="outlined" sx={{ 
                            borderColor: fileData.status === 'completed' ? 'success.main' : 
                                        fileData.status === 'error' ? 'error.main' : 
                                        fileData.status === 'uploading' ? 'info.main' : 'grey.300'
                          }}>
                            <CardContent sx={{ py: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 60 }}>
                                  <ReorderIcon color="action" fontSize="small" />
                                  <Typography variant="body2" fontWeight="bold" color="primary">
                                    Seite {fileData.sequenceNumber}
                                  </Typography>
                                </Box>
                                
                                {fileData.status === 'completed' ? (
                                  <CheckCircleIcon color="success" />
                                ) : fileData.status === 'error' ? (
                                  <ErrorIcon color="error" />
                                ) : fileData.status === 'uploading' ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <InsertDriveFileIcon color="primary" />
                                )}
                                
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1" fontWeight="medium">
                                    {fileData.file.name}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {formatFileSize(fileData.file.size)} • Status: {{
                                      pending: 'Wartend',
                                      uploading: 'Wird hochgeladen...',
                                      completed: `Hochgeladen um ${fileData.uploadedAt}`,
                                      error: 'Fehler beim Upload'
                                    }[fileData.status]}
                                  </Typography>
                                  
                                  {fileData.status === 'uploading' && (
                                    <LinearProgress
                                      variant="determinate"
                                      value={fileData.progress}
                                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                                    />
                                  )}
                                </Box>
                                
                                {fileData.status === 'pending' && (
                                  <IconButton
                                    onClick={() => removeFile(fileData.id)}
                                    color="error"
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Fade>
              )}

              {/* Overall Progress */}
              {uploading && (
                <Fade in timeout={300}>
                  <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Gesamt-Upload-Fortschritt
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={getOverallProgress()}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          mb: 1,
                        }}
                      />
                      <Typography variant="body2" textAlign="center">
                        {currentUploadIndex > 0 ? `${currentUploadIndex}/${files.filter(f => f.status === 'pending').length} Dateien` : 'Vorbereitung...'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              )}

              {/* Upload Button */}
              {hasPendingFiles && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUploadAll}
                  disabled={uploading || !selectedANRForQR}
                  fullWidth
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                    borderRadius: 2,
                    py: isMobile ? 2 : 1.5,
                    fontSize: isMobile ? '1.1rem' : '1rem',
                    fontWeight: 'bold',
                  }}
                  startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
                >
                  {uploading ? (
                    `Uploading ${currentUploadIndex}/${files.filter(f => f.status === 'pending').length}...`
                  ) : (
                    `${files.filter(f => f.status === 'pending').length} Datei(en) hochladen`
                  )}
                </Button>
              )}
              
              <Divider sx={{ my: 3 }} />
              
              {/* Help Text */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Dateien werden automatisch mit Seitennummern versehen. Haben Sie Probleme? Wenden Sie sich an den Support.
                </Typography>
              </Box>
            </CardContent>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default AnfrageInvoiceUpload;
